import React, { useState, useEffect } from 'react';
import {
  BarChart3,
  TrendingUp,
  TrendingDown,
  Users,
  ShoppingCart,
  Package,
  IndianRupee,
  Calendar,
  Download,
  RefreshCw,
  Eye,
  ArrowUpRight,
  ArrowDownRight,
  Filter
} from 'lucide-react';
import api from '../../services/api.js';

const Analytics = () => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('30'); // days
  const [selectedMetric, setSelectedMetric] = useState('revenue');

  useEffect(() => {
    fetchAnalytics();
  }, [period]);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/admin/analytics?period=${period}`);
      if (response.data.success) {
        setAnalytics(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatNumber = (num) => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    } else if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num.toString();
  };

  const StatCard = ({ title, value, change, icon: Icon, color = 'primary', trend }) => {
    const isPositive = change >= 0;
    const colorClasses = {
      primary: 'bg-primary-500',
      green: 'bg-green-500',
      blue: 'bg-blue-500',
      purple: 'bg-purple-500',
      orange: 'bg-orange-500'
    };

    return (
      <div className="bg-white rounded-lg shadow p-6 border border-gray-200">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-gray-600">{title}</p>
            <p className="text-2xl font-bold text-gray-900 mt-2">{value}</p>
            {change !== undefined && (
              <div className={`flex items-center mt-2 text-sm ${
                isPositive ? 'text-green-600' : 'text-red-600'
              }`}>
                {isPositive ? (
                  <ArrowUpRight className="w-4 h-4 mr-1" />
                ) : (
                  <ArrowDownRight className="w-4 h-4 mr-1" />
                )}
                <span>{Math.abs(change)}% from last period</span>
              </div>
            )}
          </div>
          <div className={`p-3 rounded-full ${colorClasses[color]}`}>
            <Icon className="w-6 h-6 text-white" />
          </div>
        </div>
      </div>
    );
  };

  const ChartCard = ({ title, data, type = 'bar' }) => {
    return (
      <div className="bg-white rounded-lg shadow p-6 border border-gray-200">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
          <button className="text-gray-400 hover:text-gray-600">
            <Eye className="w-5 h-5" />
          </button>
        </div>
        
        {/* Simple bar chart representation */}
        <div className="space-y-3">
          {data?.slice(0, 5).map((item, index) => {
            const maxValue = Math.max(...data.map(d => d.value || d.revenue || d.count));
            const percentage = ((item.value || item.revenue || item.count) / maxValue) * 100;
            
            return (
              <div key={index} className="flex items-center">
                <div className="w-24 text-sm text-gray-600 truncate">
                  {item.name || item._id || `Day ${index + 1}`}
                </div>
                <div className="flex-1 mx-3">
                  <div className="bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-primary-500 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${percentage}%` }}
                    ></div>
                  </div>
                </div>
                <div className="w-20 text-sm font-medium text-gray-900 text-right">
                  {typeof (item.value || item.revenue || item.count) === 'number' && 
                   (item.value || item.revenue || item.count) > 1000 
                    ? formatCurrency(item.value || item.revenue || item.count)
                    : (item.value || item.revenue || item.count)
                  }
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const TopSellerCard = ({ seller, index }) => {
    return (
      <div className="flex items-center p-4 bg-gray-50 rounded-lg">
        <div className="flex-shrink-0 w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center">
          <span className="text-sm font-semibold text-primary-600">#{index + 1}</span>
        </div>
        <div className="ml-3 flex-1">
          <p className="text-sm font-medium text-gray-900">{seller.seller?.businessName}</p>
          <p className="text-xs text-gray-600">{seller.orders} orders</p>
        </div>
        <div className="text-right">
          <p className="text-sm font-semibold text-gray-900">{formatCurrency(seller.revenue)}</p>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading analytics...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center">
              <BarChart3 className="w-8 h-8 mr-3 text-primary-600" />
              Analytics Dashboard
            </h1>
            <p className="text-gray-600 mt-1">Comprehensive platform analytics and insights</p>
          </div>
          <div className="flex space-x-3">
            <select
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500"
            >
              <option value="7">Last 7 days</option>
              <option value="30">Last 30 days</option>
              <option value="90">Last 90 days</option>
              <option value="365">Last year</option>
            </select>
            <button
              onClick={fetchAnalytics}
              className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </button>
            <button className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50">
              <Download className="w-4 h-4 mr-2" />
              Export
            </button>
          </div>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <StatCard
          title="Total Revenue"
          value={formatCurrency(analytics?.revenue?.total || 0)}
          change={12.5}
          icon={IndianRupee}
          color="green"
        />
        <StatCard
          title="Total Orders"
          value={formatNumber(analytics?.orders?.total || 0)}
          change={8.2}
          icon={ShoppingCart}
          color="blue"
        />
        <StatCard
          title="Total Customers"
          value={formatNumber(analytics?.customers?.total || 0)}
          change={15.3}
          icon={Users}
          color="purple"
        />
        <StatCard
          title="Active Sellers"
          value={formatNumber(analytics?.sellers?.total || 0)}
          change={5.7}
          icon={Package}
          color="orange"
        />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <ChartCard
          title="Revenue Trend"
          data={analytics?.revenue?.daily?.map((item, index) => ({
            name: `Day ${index + 1}`,
            revenue: item.revenue
          })) || []}
        />
        <ChartCard
          title="Orders Trend"
          data={analytics?.orders?.daily?.map((item, index) => ({
            name: `Day ${index + 1}`,
            count: item.count
          })) || []}
        />
      </div>

      {/* Detailed Analytics */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Top Categories */}
        <div className="bg-white rounded-lg shadow p-6 border border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Top Categories</h3>
            <button className="text-gray-400 hover:text-gray-600">
              <Eye className="w-5 h-5" />
            </button>
          </div>
          <div className="space-y-3">
            {analytics?.categories?.top?.slice(0, 5).map((category, index) => {
              const maxCount = Math.max(...(analytics?.categories?.top?.map(c => c.count) || [1]));
              const percentage = (category.count / maxCount) * 100;
              
              return (
                <div key={index} className="flex items-center">
                  <div className="w-20 text-sm text-gray-600 truncate">
                    {category._id?.replace('_', ' ')}
                  </div>
                  <div className="flex-1 mx-3">
                    <div className="bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-primary-500 h-2 rounded-full"
                        style={{ width: `${percentage}%` }}
                      ></div>
                    </div>
                  </div>
                  <div className="w-12 text-sm font-medium text-gray-900 text-right">
                    {category.count}
                  </div>
                </div>
              );
            }) || []}
          </div>
        </div>

        {/* Top Sellers */}
        <div className="bg-white rounded-lg shadow p-6 border border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Top Sellers</h3>
            <button className="text-gray-400 hover:text-gray-600">
              <Eye className="w-5 h-5" />
            </button>
          </div>
          <div className="space-y-3">
            {analytics?.sellers?.top?.slice(0, 5).map((seller, index) => (
              <TopSellerCard key={index} seller={seller} index={index} />
            )) || []}
          </div>
        </div>

        {/* Quick Stats */}
        <div className="bg-white rounded-lg shadow p-6 border border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Quick Stats</h3>
            <button className="text-gray-400 hover:text-gray-600">
              <Eye className="w-5 h-5" />
            </button>
          </div>
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Avg. Order Value</span>
              <span className="text-sm font-semibold text-gray-900">
                {formatCurrency((analytics?.revenue?.total || 0) / (analytics?.orders?.total || 1))}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Conversion Rate</span>
              <span className="text-sm font-semibold text-gray-900">3.2%</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Customer Retention</span>
              <span className="text-sm font-semibold text-gray-900">68%</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Avg. Delivery Time</span>
              <span className="text-sm font-semibold text-gray-900">2.5 days</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Return Rate</span>
              <span className="text-sm font-semibold text-gray-900">4.1%</span>
            </div>
          </div>
        </div>
      </div>

      {/* Performance Insights */}
      <div className="mt-8 bg-white rounded-lg shadow p-6 border border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Performance Insights</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center p-4 bg-green-50 rounded-lg">
            <TrendingUp className="w-8 h-8 text-green-600 mx-auto mb-2" />
            <h4 className="font-semibold text-green-900">Revenue Growth</h4>
            <p className="text-sm text-green-700 mt-1">
              Revenue increased by 12.5% compared to the previous period
            </p>
          </div>
          <div className="text-center p-4 bg-blue-50 rounded-lg">
            <Users className="w-8 h-8 text-blue-600 mx-auto mb-2" />
            <h4 className="font-semibold text-blue-900">Customer Acquisition</h4>
            <p className="text-sm text-blue-700 mt-1">
              New customer registrations up by 15.3% this period
            </p>
          </div>
          <div className="text-center p-4 bg-purple-50 rounded-lg">
            <Package className="w-8 h-8 text-purple-600 mx-auto mb-2" />
            <h4 className="font-semibold text-purple-900">Seller Growth</h4>
            <p className="text-sm text-purple-700 mt-1">
              Active sellers increased by 5.7% with improved engagement
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Analytics;
